import React from 'react';

// Inline stroke icons used by section headers (icon chip 34×34, 18px icon).
// Stroke widths match the design_handoff spec (1.7–1.8 for chrome glyphs).
export const SectionIcons = {
    box: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 2.6 20.5 7v10L12 21.4 3.5 17V7z" />
            <path d="M3.7 7 12 11.5 20.3 7M12 21.4V11.5" />
        </svg>
    ),
    image: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2.5" />
            <circle cx="8.5" cy="9.5" r="1.8" />
            <path d="m4 17 4.5-4.5 4 4 3-3L20 17" />
        </svg>
    ),
    cog: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3.2" />
            <path d="M12 2.5v3M12 18.5v3M21.5 12h-3M5.5 12h-3M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1M18.7 18.7l-2.1-2.1M7.4 7.4 5.3 5.3" />
        </svg>
    ),
    flow: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="6" height="6" rx="1.5" />
            <rect x="15" y="15" width="6" height="6" rx="1.5" />
            <path d="M9 6h6a3 3 0 0 1 3 3v6" />
        </svg>
    ),
    layers: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m12 3 9 5-9 5-9-5z" />
            <path d="m3 13 9 5 9-5" />
        </svg>
    ),
    wrench: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14.5 6.5a3.5 3.5 0 0 0-4.7 4.3l-6 6 2.1 2.1 6-6a3.5 3.5 0 0 0 4.3-4.7l-2.2 2.2-1.4-1.4z" />
        </svg>
    ),
    truck: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2.5" y="6" width="11" height="9" rx="1.5" />
            <path d="M13.5 9H17l3.5 3.5V15h-7" />
            <circle cx="7" cy="17.5" r="1.8" />
            <circle cx="17" cy="17.5" r="1.8" />
        </svg>
    ),
};

export type SectionIconName = keyof typeof SectionIcons;

interface SectionCardProps {
    icon: SectionIconName | React.ReactNode;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    right?: React.ReactNode;
    className?: string;
    children: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({
    icon,
    title,
    subtitle,
    right,
    className = '',
    children,
}) => {
    const renderedIcon = typeof icon === 'string' && icon in SectionIcons
        ? SectionIcons[icon as SectionIconName]
        : (icon as React.ReactNode);

    return (
        <section className={`art-form-section ${className}`}>
            <div className="art-form-section__header">
                <div className="art-form-section__icon" aria-hidden="true">
                    {renderedIcon}
                </div>
                <div className="art-form-section__title-group">
                    <h3 className="art-form-section__title">{title}</h3>
                    {subtitle && <p className="art-form-section__subtitle">{subtitle}</p>}
                </div>
                {right && <div className="art-form-section__right">{right}</div>}
            </div>
            <div className="art-form-section__body">{children}</div>
        </section>
    );
};

export default SectionCard;
